import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Stack,
  Box,
  Snackbar,
  Alert,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  MenuItem,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";

import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from "@mui/icons-material";
import axios from "axios";

import { useNavigate } from "react-router-dom";
import { validateComplexPassword } from "../../utils/passwordValidation";

const USER_URL = `${process.env.REACT_APP_API_URL}/api/tele`;
const TICKET_URL = `${process.env.REACT_APP_API_URL}/api/tele`;

const EMPTY_USER = {
  name: "",
  username: "",
  password: "",
  primaryContact: "",
  secondaryContact: "",
};

const EMPTY_TICKET = {
  serviceNumber: "",
  status: "Open",
  assignedTo: "",
  ticketStatus: "Open",
  description: "",
};

const TeleAdminDashboard = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  // States
  const [users, setUsers] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [newUser, setNewUser] = useState(EMPTY_USER);
  const [newTicket, setNewTicket] = useState(EMPTY_TICKET);
  const [editUserIndex, setEditUserIndex] = useState(null);
  const [editTicketIndex, setEditTicketIndex] = useState(null);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showChangePass, setShowChangePass] = useState(false);
  const [oldPass, setOldPass] = useState("");
  const [newPass, setNewPass] = useState("");
  const [confirmPass, setConfirmPass] = useState("");
  const [tabIndex, setTabIndex] = useState(0);
  const [userSearch, setUserSearch] = useState("");
  const [ticketSearch, setTicketSearch] = useState("");
  const [userLoading, setUserLoading] = useState(false);
  const [ticketLoading, setTicketLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Row selection
  const [selectedUserIndexes, setSelectedUserIndexes] = useState([]);
  const [selectedTicketIndexes, setSelectedTicketIndexes] = useState([]);
  const [ticketStatusFilter, setTicketStatusFilter] = useState("Open");

  // Snackbar helpers
  const showSnackbar = (msg, severity = "success") =>
    setSnackbar({ open: true, message: msg, severity });
  const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

  // ===== Fetch Users =====
  const fetchUsers = useCallback(async () => {
    try {
      setUserLoading(true);
      const res = await axios.get(`${USER_URL}/users/all`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      showSnackbar(err.response?.data?.msg || "Failed to fetch users", "error");
      setUsers([]);
    } finally {
      setUserLoading(false);
    }
  }, [token]);

  // ===== Fetch Tickets =====
  const fetchTickets = useCallback(async () => {
    try {
      setTicketLoading(true);
      const res = await axios.get(`${TICKET_URL}/tickets/all`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTickets(Array.isArray(res.data?.tickets) ? res.data.tickets : []);
    } catch (err) {
      showSnackbar(
        err.response?.data?.msg || "Failed to fetch tickets",
        "error"
      );
      setTickets([]);
    } finally {
      setTicketLoading(false);
    }
  }, [token]);

  // Load data on mount
  useEffect(() => {
    fetchUsers();
    fetchTickets();
  }, [fetchUsers, fetchTickets]);

  // ===== User Handlers =====
  const handleUserChange = (e) =>
    setNewUser({ ...newUser, [e.target.name]: e.target.value });

  const saveUser = async () => {
    if (
      !newUser.name ||
      !newUser.username ||
      (!newUser.password && editUserIndex === null)
    )
      return showSnackbar("Name, Username and Password are required", "error");

    try {
      if (editUserIndex !== null) {
        const userId = users[editUserIndex]._id;
        const res = await axios.put(`${USER_URL}/${userId}`, newUser, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const updated = [...users];
        updated[editUserIndex] = res.data.user || res.data;
        setUsers(updated);
        setEditUserIndex(null);
        showSnackbar("User updated successfully!");
      } else {
        const res = await axios.post(`${USER_URL}/create`, newUser, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUsers((prev) => [...prev, res.data.user]);
        showSnackbar(res.data.msg || "User created successfully!");
        setShowCreateUser(false);
      }
      setNewUser(EMPTY_USER);
    } catch (err) {
      showSnackbar(err.response?.data?.msg || "Failed to save user", "error");
    }
  };

  const editUser = (index) => {
    const user = users[index];
    setNewUser({ ...user, password: "" });
    setEditUserIndex(index);
    setShowCreateUser(true);
    setSelectedUserIndexes([index]);
  };

  const deleteUser = async (index) => {
    if (!window.confirm(`Delete user ${users[index].username}?`)) return;
    try {
      await axios.delete(`${USER_URL}/${users[index]._id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers((prev) => prev.filter((_, i) => i !== index));
      setSelectedUserIndexes((prev) => prev.filter((i) => i !== index));
      showSnackbar("User deleted successfully!");
    } catch (err) {
      showSnackbar(err.response?.data?.msg || "Failed to delete user", "error");
    }
  };

  // ===== Universal User Search =====
  const filteredUsers = Array.isArray(users)
    ? users
        .filter(Boolean)
        .filter((u) =>
          Object.values(u)
            .join(" ")
            .toLowerCase()
            .includes(userSearch.toLowerCase())
        )
    : [];

  // ===== Ticket Handlers =====
  const handleTicketChange = (e) =>
    setNewTicket({ ...newTicket, [e.target.name]: e.target.value });

  const saveTicket = async () => {
    if (!newTicket.serviceNumber || !newTicket.status)
      return showSnackbar("Service number and status are required", "error");

    try {
      if (editTicketIndex !== null) {
        const ticketId = tickets[editTicketIndex]._id;
        const res = await axios.put(
          `${TICKET_URL}/ticket/${ticketId}`,
          newTicket,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setTickets((prev) =>
          prev.map((t, i) => (i === editTicketIndex ? res.data.ticket || t : t))
        );
        setEditTicketIndex(null);
        showSnackbar("Ticket updated successfully!");
      } else {
        const res = await axios.post(`${TICKET_URL}/create/ticket`, newTicket, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setTickets((prev) => [...prev, res.data.ticket]);
        showSnackbar("Ticket created successfully!");
      }
      setNewTicket(EMPTY_TICKET);
    } catch (err) {
      showSnackbar(err.response?.data?.msg || "Failed to save ticket", "error");
    }
  };

  // ===== Universal Ticket Search =====
  const filteredTickets = Array.isArray(tickets)
    ? tickets
        .filter(Boolean)
        .filter((t) =>
          Object.values(t)
            .join(" ")
            .toLowerCase()
            .includes(ticketSearch.toLowerCase())
        )
    : [];

  // ===== Password Change =====
  const handleChangePassword = async () => {
    if (!oldPass || !newPass || !confirmPass)
      return showSnackbar("All fields are required", "error");
    if (newPass !== confirmPass)
      return showSnackbar("Passwords do not match", "error");

    const validation = validateComplexPassword(newPass);
    if (!validation.valid) {
      return showSnackbar(validation.msg, "error");
    }

    try {
      const res = await axios.patch(
        `${USER_URL}/change-password`,
        { oldPassword: oldPass, newPassword: newPass },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSnackbar(res.data.msg || "Password changed successfully!");
      setShowChangePass(false);
      setOldPass("");
      setNewPass("");
      setConfirmPass("");
    } catch (err) {
      showSnackbar(
        err.response?.data?.msg || "Failed to change password",
        "error"
      );
    }
  };

  // ===== Row Selection Helper =====
  const handleRowSelection = (index, type, event) => {
    const isShift = event.shiftKey;
    const isCtrl = event.ctrlKey || event.metaKey; // Cmd key for Mac
    let selectedArray =
      type === "user" ? [...selectedUserIndexes] : [...selectedTicketIndexes];

    if (isShift && selectedArray.length > 0) {
      const lastIndex = selectedArray[selectedArray.length - 1];
      const [start, end] =
        lastIndex < index ? [lastIndex, index] : [index, lastIndex];
      const range = Array.from(
        { length: end - start + 1 },
        (_, i) => i + start
      );
      selectedArray = Array.from(new Set([...selectedArray, ...range]));
    } else if (isCtrl) {
      selectedArray.includes(index)
        ? selectedArray.splice(selectedArray.indexOf(index), 1)
        : selectedArray.push(index);
    } else {
      selectedArray = [index];
    }

    type === "user"
      ? setSelectedUserIndexes(selectedArray)
      : setSelectedTicketIndexes(selectedArray);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 5, mb: 5 }}>
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={4}
      >
        <Typography variant="h4" fontWeight="bold">
          Tele Admin Dashboard
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button variant="outlined" onClick={() => setShowChangePass(true)}>
            Change
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={() => {
              localStorage.removeItem("token");
              navigate("/login");
            }}
          >
            Logout
          </Button>
        </Stack>
      </Box>

      {/* Tabs */}
      <Tabs
        value={tabIndex}
        onChange={(_, value) => setTabIndex(value)}
        sx={{ mb: 2 }}
      >
        <Tab label="Technicians" />
        <Tab label="Tickets" />
      </Tabs>

      {/* ===== Users Tab ===== */}
      {tabIndex === 0 && (
        <>
          {/* User Form */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="h6">
                {editUserIndex !== null ? "Edit User" : "Create User"}
              </Typography>
              <IconButton onClick={() => setShowCreateUser((prev) => !prev)}>
                {showCreateUser ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>

            <Collapse in={showCreateUser}>
              {/* ===== Row 1 ===== */}
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                mb={2}
                mt={2}
                flexWrap="wrap"
              >
                <TextField
                  label="Name"
                  name="name"
                  value={newUser.name}
                  onChange={handleUserChange}
                  size="small"
                />
                <TextField
                  label="Username"
                  name="username"
                  value={newUser.username}
                  onChange={handleUserChange}
                  size="small"
                />
                <TextField
                  label="Password"
                  name="password"
                  type="password"
                  value={newUser.password}
                  onChange={handleUserChange}
                  size="small"
                />
                <TextField
                  label="Primary Contact"
                  name="primaryContact"
                  value={newUser.primaryContact}
                  onChange={handleUserChange}
                  size="small"
                />
              </Stack>

              {/* ===== Row 2 ===== */}
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                mb={2}
                flexWrap="wrap"
              >
                <TextField
                  label="Secondary Contact"
                  name="secondaryContact"
                  value={newUser.secondaryContact}
                  onChange={handleUserChange}
                  size="small"
                />

                <Button variant="contained" onClick={saveUser}>
                  {editUserIndex !== null ? "Update" : "Create"}
                </Button>

                {editUserIndex !== null && (
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => setEditUserIndex(null)}
                  >
                    Cancel
                  </Button>
                )}

              </Stack>
            </Collapse>
          </Paper>

          {/* ===== User Table ===== */}
          <Paper>
            <Box p={2}>
              <Box
                display="flex"
                alignItems="center"
                gap={1}
                mb={2}
                flexWrap="wrap"
              >
                <TextField
                  label="Search User"
                  size="small"
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  sx={{ width: 250 }}
                />
                <Button
                  size="small"
                  startIcon={<RefreshIcon />}
                  onClick={fetchUsers}
                  disabled={userLoading}
                />
                <Button
                  variant="contained"
                  color="error"
                  disabled={selectedUserIndexes.length === 0}
                  onClick={() => {
                    if (window.confirm("Delete selected users?")) {
                      selectedUserIndexes.forEach((i) => deleteUser(i));
                      setSelectedUserIndexes([]);
                    }
                  }}
                >
                  Delete Selected
                </Button>
              </Box>

              {userLoading ? (
                <Box display="flex" justifyContent="center" p={4}>
                  <CircularProgress />
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Username</TableCell>
                        <TableCell>Primary Contact</TableCell>
                        <TableCell>Secondary Contact</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredUsers.map((u, i) => (
                        <TableRow
                          key={u._id}
                          onClick={(e) => handleRowSelection(i, "user", e)}
                          sx={{
                            backgroundColor: selectedUserIndexes.includes(i)
                              ? "rgba(25, 118, 210, 0.15)"
                              : "inherit",
                            cursor: "pointer",
                            "&:hover": {
                              backgroundColor: selectedUserIndexes.includes(i)
                                ? "rgba(25, 118, 210, 0.25)"
                                : "rgba(0,0,0,0.04)",
                            },
                          }}
                        >
                          <TableCell>{u.name}</TableCell>
                          <TableCell>{u.username}</TableCell>
                          <TableCell>{u.primaryContact || "-"}</TableCell>
                          <TableCell>{u.secondaryContact || "-"}</TableCell>
                          <TableCell>
                            <IconButton
                              color="primary"
                              onClick={(e) => {
                                e.stopPropagation();
                                editUser(i);
                              }}
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              color="error"
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteUser(i);
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </Paper>
        </>
      )}

      {/* ===== Tickets Tab ===== */}
{tabIndex === 1 && (
  <>
    {/* ===== First Row: Search / Refresh / Delete Selected / Status Filter ===== */}
    <Paper sx={{ p: 2, mb: 2 }}>
      <Box
        display="flex"
        alignItems="center"
        gap={2}
        flexWrap="wrap"
      >
        <TextField
          label="Search"
          size="small"
          value={ticketSearch}
          onChange={(e) => setTicketSearch(e.target.value)}
        />
        <Button
          size="small"
          color="primary"
          onClick={fetchTickets}
          startIcon={<RefreshIcon />}
        >
      
        </Button>

        {/* ===== Ticket Status Selector ===== */}
        <TextField
          select
          label="Ticket Status"
          size="small"
          value={ticketStatusFilter}
          onChange={(e) => setTicketStatusFilter(e.target.value)}
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="Open">Open</MenuItem>
          <MenuItem value="Closed">Closed</MenuItem>
          <MenuItem value="All">All</MenuItem>
        </TextField>
      </Box>
    </Paper>

    {/* ===== Second Row: Edit Ticket Row ===== */}
    {editTicketIndex !== null && (
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" mb={2}>
          Edit Ticket
        </Typography>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          flexWrap="wrap"
          alignItems="center"
        >
          <TextField
            label="Service Number"
            name="serviceNumber"
            value={newTicket.serviceNumber}
            size="small"
            InputProps={{ readOnly: true }}
          />
          <TextField
            label="CCT Number"
            name="CCTNumber"
            value={newTicket.CCTNumber || ""}
            onChange={handleTicketChange}
            size="small"
          />
          {/* Assigned Technician Selector */}
          <TextField
            select
            label="Assigned Technician"
            name="assignedTechnician"
            value={newTicket.assignedTechnician || ""}
            onChange={handleTicketChange}
            size="small"
            sx={{ minWidth: 200 }}
          >
            {users.map((u) => (
              <MenuItem key={u._id} value={u.name}>
                {u.name} ({u.username})
              </MenuItem>
            ))}
          </TextField>
          <Button variant="contained" onClick={saveTicket}>
            Update
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => setEditTicketIndex(null)}
          >
            Cancel
          </Button>
        </Stack>
      </Paper>
    )}

    {/* ===== Tickets Table ===== */}
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Company</TableCell>
            <TableCell>Service No</TableCell>
            <TableCell>WAN IP</TableCell>
            <TableCell>Pri.Contact</TableCell>
            <TableCell>Sec.Contact</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Ticket Status</TableCell>
            <TableCell>CCT Number</TableCell>
            <TableCell>Technician</TableCell>
            <TableCell>Created At</TableCell>
            <TableCell>Closed At</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredTickets
            .filter((t) => {
              if (ticketStatusFilter === "All") return true;
              return t.ticketStatus?.toLowerCase() === ticketStatusFilter.toLowerCase();
            })
            .map((t, i) => (
              <TableRow
                key={t._id}
                onClick={(e) => handleRowSelection(i, "ticket", e)}
                sx={{
                  backgroundColor: selectedTicketIndexes.includes(i)
                    ? "rgba(25, 118, 210, 0.15)"
                    : "inherit",
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: selectedTicketIndexes.includes(i)
                      ? "rgba(25, 118, 210, 0.25)"
                      : "rgba(0,0,0,0.04)",
                  },
                }}
              >
                <TableCell>{t.name || "N/A"}</TableCell>
                <TableCell>{t.company || "N/A"}</TableCell>
                <TableCell>{t.serviceNumber || "N/A"}</TableCell>
                <TableCell>{t.wanIP || "N/A"}</TableCell>
                <TableCell>{t.primaryContact || "N/A"}</TableCell>
                <TableCell>{t.secondaryContact || "N/A"}</TableCell>
                <TableCell>{t.status || "N/A"}</TableCell>
                <TableCell>{t.ticketStatus || "N/A"}</TableCell>
                <TableCell>{t.CCTNumber || "N/A"}</TableCell>
                <TableCell>{t.assignedTechnician || "N/A"}</TableCell>
                <TableCell>{new Date(t.createdAt).toLocaleString()}</TableCell>
                <TableCell>{t.closedAt ? new Date(t.closedAt).toLocaleString() : "N/A"}</TableCell>
                <TableCell>
                  <IconButton
                    color="primary"
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditTicketIndex(i);
                      setNewTicket({ ...t });
                    }}
                  >
                    <EditIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
    </TableContainer>
  </>
)}


      {/* ===== Change Password Dialog ===== */}
      <Dialog open={showChangePass} onClose={() => setShowChangePass(false)}>
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              label="Old Password"
              type="password"
              value={oldPass}
              onChange={(e) => setOldPass(e.target.value)}
              fullWidth
            />
            <TextField
              label="New Password"
              type="password"
              value={newPass}
              onChange={(e) => setNewPass(e.target.value)}
              fullWidth
            />
            <TextField
              label="Confirm Password"
              type="password"
              value={confirmPass}
              onChange={(e) => setConfirmPass(e.target.value)}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleChangePassword} variant="contained">
            Change
          </Button>
          <Button onClick={() => setShowChangePass(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default TeleAdminDashboard;
